import { store } from '../../store'
import config from '../../config'
import { refreshAccessToken } from '@/actions/auth'
import { isAccessTokenExpired } from '@/reducers'
import { err, Err, ok, Result } from 'neverthrow'

type Options = RequestInit & {
  attemptRefreshWhenExpired?: boolean
}

const defaultOptions: Options = {
  method: 'GET',
  mode: 'cors' as RequestMode,
  attemptRefreshWhenExpired: true,
  credentials: 'include',
}

/**
 *
 * @param apiEndpoint - endpoint prepended to the api gateway url, with optional leading and trailing slashes
 * @param options - options identical to the `fetch` API options
 * @param searchParams - Optional URL search parameters to append to the request
 * @returns - `fetch` API response Promise
 */
export function fetchAuthenticated(
  apiEndpoint: string,
  options?: Options,
  searchParams?: URLSearchParams | string,
): Promise<Response> {
  const fetchOptions = { ...defaultOptions, ...options }
  const authToken = store.getState()?.auth?.access?.token
  const refreshToken = store.getState()?.auth?.refresh?.token

  const tokenIsExpired = isAccessTokenExpired(store.getState())
  if (tokenIsExpired && fetchOptions.attemptRefreshWhenExpired) {
    return store.dispatch(refreshAccessToken(refreshToken)).then(() => {
      // set attemptRefreshWhenExpired to false to prevent infinite recursion
      return fetchAuthenticated(apiEndpoint, {
        ...options,
        attemptRefreshWhenExpired: false,
      })
    })
  }

  const endpoint = `${apiEndpoint.startsWith('/') ? '' : '/'}${apiEndpoint}${
    apiEndpoint.endsWith('/') ? '' : '/'
  }`

  return fetch(
    `${config.apiGateway.URL}/api${endpoint}${
      searchParams ? `?${searchParams.toString()}` : ''
    }`,
    {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...fetchOptions.headers,
      },
    },
  )
}

/**
 * Performs an authenticated API request and returns a Result type containing either the parsed JSON data or the error response.
 *
 * This function wraps `fetchAuthenticated` with automatic JSON parsing and error handling using the Result pattern.
 *
 * @template T - The expected type of the successful JSON response data
 * @param apiEndpoint - API endpoint path to call (will be prepended with the API gateway URL and /api prefix)
 * @param options - Optional fetch options (extends RequestInit with attemptRefreshWhenExpired flag)
 * @param searchParams - Optional URL search parameters to append to the request
 * @returns A Promise resolving to either:
 *   - ok(data): Successfully parsed JSON response of type T
 *   - err(Response): The raw Response object if the request failed (non-2xx status)
 *
 * @example
 * const result = await fetchAuthenticatedJSONResult<User>('/current_user')
 * // check or map() the error
 * if (result.isErr()) {
 *   console.error('Error status:', result.error.status)
 *   return
 * }
 * console.log('User:', result.value)
 */
export async function fetchAuthenticatedJSONResult<T>(
  apiEndpoint: string,
  options?: Options,
  searchParams?: URLSearchParams | string,
): Promise<Result<T, Response>> {
  const resp = await fetchAuthenticated(apiEndpoint, options, searchParams)
  if (!resp.ok) {
    return err(resp)
  }
  const data = (await resp.json()) as T
  return ok(data)
}
